import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Own requests per table so late responses cannot replace another page/report.
export default function PaginatedReportTable({ visualization, requestScope, loadPage, renderTable }) {
  const [options, setOptions] = useState({ page: 1, pageSize: 50, sort: {} });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const loader = useRef(loadPage);
  loader.current = loadPage;
  useEffect(() => {
    const controller = new AbortController();
    const current = ++generation.current;
    setLoading(true);
    setError("");
    const fetchPage = async () => {
      for (let attempt = 0; ; attempt += 1) {
        try { return await loader.current(options, false, controller.signal); }
        catch (err) {
          if (err.status !== 429 || attempt >= 5 || controller.signal.aborted) throw err;
          await new Promise((resolve, reject) => {
            const onAbort = () => { clearTimeout(timer); reject(new Error("Aborted")); };
            const timer = setTimeout(() => { controller.signal.removeEventListener("abort", onAbort); resolve(); }, 1000 * (attempt + 1));
            controller.signal.addEventListener("abort", onAbort, { once: true });
          });
        }
      }
    };
    fetchPage().then((payload) => {
      if (current === generation.current) setResult(payload.result);
    }).catch((err) => {
      if (!controller.signal.aborted && current === generation.current) setError(err.message);
    }).finally(() => {
      if (current === generation.current) setLoading(false);
    });
    return () => { controller.abort(); generation.current += 1; };
  }, [requestScope, options, retry]);
  const downloadFull = async () => {
    setExporting(true);
    setNotice("");
    try {
      const payload = await loader.current(options, true);
      setNotice(payload.message);
    } catch (err) { setNotice(err.message); }
    finally { setExporting(false); }
  };
  return <div className="h-full min-h-0 flex flex-col">
    {error ? <div role="alert" className="p-3 text-red-700">{error} <button onClick={() => setRetry((n) => n + 1)}>Retry</button></div> : null}
    {notice ? <div role="status" className="px-3 text-sm">{notice}</div> : null}
    {loading ? <div role="status" className="p-2 text-sm">Loading table…</div> : null}
    <div className="flex-1 min-h-0" aria-busy={loading}>
      {result && !loading && !error ? renderTable({ ...visualization, ...result, config: visualization.config, data: result.data }, {
        sort: options.sort,
        onSort: (field) => setOptions((old) => ({ ...old, page: 1, sort: {
          field, direction: old.sort.field === field && old.sort.direction === "asc" ? "desc" : "asc",
        } })),
        downloadFull, exporting,
      }) : null}
    </div>
    <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 text-sm">
      <label className="flex items-center gap-2 text-gray-600">Rows per page
        <select aria-label="Rows per page" value={options.pageSize} disabled={loading}
          className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
          onChange={(e) => setOptions((old) => ({ ...old, page: 1, pageSize: Number(e.target.value) }))}>
          {[25, 50, 100].map((size) => <option key={size}>{size}</option>)}
        </select>
      </label>
      <nav aria-label="Table pagination" className="flex items-center gap-3">
        <span className="whitespace-nowrap text-gray-600">Page <span className="font-medium text-gray-900">{options.page}</span></span>
        <div className="inline-flex rounded-md shadow-sm">
          <button type="button" aria-label="Previous page" disabled={loading || options.page === 1}
            className="inline-flex h-8 items-center gap-1 rounded-l-md border border-gray-300 bg-white px-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-300"
            onClick={() => setOptions((old) => ({ ...old, page: old.page - 1 }))}>
            <ChevronLeft className="h-4 w-4" />Previous
          </button>
          <button type="button" aria-label="Next page" disabled={loading || !!error || !result?.data?.pagination?.hasNext}
            className="-ml-px inline-flex h-8 items-center gap-1 rounded-r-md border border-gray-300 bg-white px-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-300"
            onClick={() => setOptions((old) => ({ ...old, page: old.page + 1 }))}>
            Next<ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </nav>
    </div>
  </div>;
}
